/**
 * Lenis Scroll Utility
 * 
 * This module provides global access to the Lenis smooth scrolling instance
 * and utility functions for controlling scroll behavior throughout the application.
 * 
 * The Lenis instance is created in the useSmoothScroll hook and stored here
 * to be accessible from any component without having to pass props or use context.
 */
import Lenis from 'lenis';

// Global lenis instance that can be accessed throughout the app
let lenisInstance: Lenis | null = null;

/**
 * Sets the global Lenis instance
 * @param instance - The Lenis instance to store globally
 */
export const setLenisInstance = (instance: Lenis | null) => {
  lenisInstance = instance;
};

/**
 * Gets the global Lenis instance
 * @returns The current Lenis instance or null if not initialized
 */
export const getLenisInstance = (): Lenis | null => {
  return lenisInstance;
};

/**
 * Scrolls to a specific element, position, or selector
 * @param target - The target to scroll to (element ID, DOM element, or y-position)
 * @param options - Optional configuration for the scroll behavior
 *   - offset: number - Additional offset from the target
 *   - duration: number - Duration of the animation in seconds
 *   - lock: boolean - Whether to lock scrolling during animation
 *   - lerp: number - Linear interpolation factor
 *   - onComplete: () => void - Callback when scrolling is complete
 */
export const scrollTo = (target: string | HTMLElement | number, options?: any) => {
  if (lenisInstance) {
    lenisInstance.scrollTo(target, options);
  }
};

/**
 * Stops the smooth scrolling behavior
 */
export const stop = () => {
  if (lenisInstance) {
    lenisInstance.stop();
  }
};

/**
 * Starts the smooth scrolling behavior
 */
export const start = () => {
  if (lenisInstance) {
    lenisInstance.start();
  }
}; 